import { Option } from '@code/innovance-react-ui-kit';

import { CustomerIdentityData, DocumentPageIdentityData } from 'src/front/api/generated';
import { IdentityFormData } from 'src/front/components/identity/IdentityFormData';
import { convertStringToDate } from './dateTimeUtils';
import {
  formatAlphaNumericCyrillicPlusDashAndBlank,
  formatFreeText,
  formatNumeric,
} from './formUtils';

export const FORMATTER = {
  NUMERIC: formatNumeric,
  ALPHANUMERIC: formatAlphaNumericCyrillicPlusDashAndBlank,
  FREETEXT: formatFreeText,
};

export interface IdentityFieldRules {
  minimumLength?: number;
  maximumLength?: number;
  maskFormat?: string;
  enabled: boolean;
  required: boolean;
  visible: boolean;
  dataType?: (value: string) => string;
  isDefaultCountryRf?: boolean;
}

export interface IdentityFields {
  series: IdentityFieldRules;
  documentNumber: IdentityFieldRules;
  issueDate: IdentityFieldRules;
  expireDate: IdentityFieldRules;
  issuedBy: IdentityFieldRules;
  country: IdentityFieldRules;
  unitCode: IdentityFieldRules;
  inn?: IdentityFieldRules;
}

export interface IdentityRules {
  [key: string]: IdentityFields;
}

export const FORM_RULES: IdentityRules = {
  // Паспорт гражданина РФ
  PS: {
    series: {
      minimumLength: 4,
      maximumLength: 4,
      dataType: formatNumeric,
      enabled: true,
      required: true,
      visible: true,
    },
    documentNumber: {
      minimumLength: 6,
      maximumLength: 6,
      dataType: formatNumeric,
      enabled: true,
      required: true,
      visible: true,
    },
    issueDate: {
      enabled: true,
      required: true,
      visible: true,
    },
    expireDate: {
      enabled: false,
      required: false,
      visible: false,
    },
    issuedBy: {
      minimumLength: 1,
      maximumLength: 200,
      dataType: formatFreeText,
      enabled: true,
      required: true,
      visible: true,
    },
    country: {
      enabled: true,
      required: true,
      visible: true,
    },
    unitCode: {
      minimumLength: 6,
      maximumLength: 6,
      dataType: formatNumeric,
      enabled: true,
      required: true,
      visible: true,
      maskFormat: 'XXX-XXX',
    },
  },
  // Водительское удостоверение
  DL: {
    series: {
      minimumLength: 4,
      maximumLength: 4,
      dataType: formatNumeric,
      enabled: true,
      required: true,
      visible: true,
    },
    documentNumber: {
      minimumLength: 6,
      maximumLength: 6,
      dataType: formatNumeric,
      enabled: true,
      required: true,
      visible: true,
    },
    issueDate: {
      enabled: true,
      required: true,
      visible: true,
    },
    expireDate: {
      enabled: true,
      required: true,
      visible: true,
    },
    issuedBy: {
      minimumLength: 1,
      maximumLength: 200,
      dataType: formatFreeText,
      enabled: true,
      required: true,
      visible: true,
    },
    country: {
      enabled: true,
      required: true,
      visible: true,
    },
    unitCode: {
      minimumLength: 0,
      maximumLength: 6,
      dataType: formatAlphaNumericCyrillicPlusDashAndBlank,
      enabled: true,
      required: true,
      visible: true,
    },
  },
  // Страховое свидетельство ГПФ
  IC: {
    series: {
      minimumLength: 0,
      maximumLength: 0,
      dataType: formatAlphaNumericCyrillicPlusDashAndBlank,
      enabled: false,
      required: false,
      visible: false,
    },
    documentNumber: {
      minimumLength: 11,
      maximumLength: 11,
      dataType: formatNumeric,
      enabled: true,
      required: true,
      visible: true,
      maskFormat: 'XXX-XXX-XXX XX',
    },
    issueDate: {
      enabled: false,
      required: false,
      visible: false,
    },
    expireDate: {
      enabled: false,
      required: false,
      visible: false,
    },
    issuedBy: {
      minimumLength: 1,
      maximumLength: 200,
      dataType: formatFreeText,
      enabled: false,
      required: false,
      visible: false,
    },
    country: {
      enabled: true,
      required: true,
      visible: true,
    },
    unitCode: {
      enabled: false,
      required: false,
      visible: false,
    },
  },
  // // Свидетельство о присвоении ИНН
  // CT: {},
  // // Вид на жительство в РФ
  // RP: {},
  // // Виза
  // VS: {},
  // // Миграционная карта
  // MR: {},
  // // Заграничный паспорт для граждан РФ
  // FP: {},
  // // Разрешение на временное проживание
  // RR: {},
  // // Паспорт моряка
  // SP: {},
  // // Паспорт иностранного гражданина
  // PT: {},
};
const defaultIdentityValue = {
  identityType: null,
  identityDocumentSerialCode: '',
  identityDocumentSerialNumber: '',
  issueDate: null,
  expireDate: null,
  issuedBy: '',
  issueCountry: null,
  unitCode: '',
};

interface Identity extends IdentityFormData {
  isNew?: boolean;
  givenCountry?: string;
}

export const convertDocumentIdentityDataToCustomerIdentityData = (
  data?: DocumentPageIdentityData[]
): CustomerIdentityData[] => {
  const identityList =
    data?.map((identity) => ({
      customerIdentityId: identity.customerIdentityId || '',
      series: identity.identityDocumentSerialCode || '',
      issueDate: identity.issueDate || '',
      expirationDate: identity.expireDate,
      identityType: identity.identityType,
      issuedBy: identity.issuedBy || '',
      unitCode: identity.unitCode,
      issueCountry: identity.issueCountry,
      isActive: identity.isActive,
      documentType: identity.identityType.value,
      hasUsed: false,
      serialCode: identity.identityDocumentSerialNumber || '',
    })) || [];
  return identityList;
};
export const getIdentityList = (
  identityTypes: Option[],
  data?: CustomerIdentityData[]
): Identity[] => {
  const identityList =
    data?.map((identity: CustomerIdentityData) => ({
      ...defaultIdentityValue,
      identityDocumentSerialCode: identity.series,
      identityDocumentSerialNumber: identity.serialCode,
      issueDate: convertStringToDate(identity.issueDate),
      expireDate: convertStringToDate(identity.expirationDate),
      identityType: identityTypes.find((type) => type.value === identity.documentType) || {
        value: identity.documentType,
        label: identity.documentType,
      },
      id: identity.customerIdentityId,
      issuedBy: identity.issuedBy,
      unitCode: identity.unitCode || '',
      isNew: false,
      givenCountry: identity.givenCountry,
      isActive: identity.isActive,
    })) || [];

  return identityList;
};
